import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useSectionAccess } from '../hooks/useSectionAccess';
import { 
  getFinalSettlements, createFinalSettlement, approveFinalSettlement, 
  rejectFinalSettlement, markSettlementProcessed, downloadFinalSettlementPDF,
  getFinalSettlementStats, getEmployees 
} from '../api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '../components/ui/command';
import { Check, ChevronsUpDown } from 'lucide-react';
import { 
  UserMinus, Plus, FileText, CheckCircle, XCircle, Clock, 
  DollarSign, Calendar, AlertCircle, Download, Eye, Filter,
  TrendingUp, Users, Banknote, CreditCard, ArrowLeft, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';

const statusColors = {
  pending: 'bg-amber-100 text-amber-700 border-amber-200',
  approved: 'bg-blue-100 text-blue-700 border-blue-200',
  processed: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  rejected: 'bg-red-100 text-red-700 border-red-200'
};

const separationTypes = [
  { value: 'resignation', label: 'Resignation' },
  { value: 'termination', label: 'Termination' },
  { value: 'retirement', label: 'Retirement' },
  { value: 'contract_end', label: 'Contract End' },
  { value: 'absconding', label: 'Absconding' }
];

const FinalSettlement = ({ user, setUser }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const prefillData = location.state?.prefillData;
  
  const [settlements, setSettlements] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedSettlement, setSelectedSettlement] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  
  // Asset check state
  const [pendingAssets, setPendingAssets] = useState([]);
  const [assetsChecked, setAssetsChecked] = useState(false);
  const [checkingAssets, setCheckingAssets] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    employee_id: '',
    separation_type: 'resignation',
    last_working_date: '',
    resignation_date: '',
    total_leave_balance: 0,
    leave_encashment_days: 0,
    notice_period_required_days: 60,
    notice_period_served_days: 0,
    notice_period_buyout: false,
    pending_reimbursements: 0,
    bonus_earned: 0,
    incentive_earned: 0,
    other_earnings: 0,
    other_earnings_remarks: '',
    loan_outstanding: 0,
    advance_outstanding: 0,
    notice_recovery: 0,
    other_deductions: 0,
    other_deductions_remarks: '',
    remarks: '',
    exit_id: ''
  });

  useEffect(() => {
    fetchData();
  }, [filterStatus]);

  // Handle prefilled data from Exit Management
  useEffect(() => {
    if (prefillData?.from_exit) {
      setFormData(prev => ({
        ...prev,
        employee_id: prefillData.employee_id || '',
        separation_type: prefillData.separation_type || 'resignation',
        resignation_date: prefillData.resignation_date?.split('T')[0] || '',
        last_working_date: prefillData.last_working_date?.split('T')[0] || '',
        exit_id: prefillData.exit_id || ''
      }));
      setShowCreateModal(true);
      // Clear the navigation state
      window.history.replaceState({}, document.title);
    }
  }, [prefillData]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [settlementsRes, employeesRes, statsRes] = await Promise.all([
        getFinalSettlements(filterStatus !== 'all' ? filterStatus : null),
        getEmployees(),
        getFinalSettlementStats()
      ]);
      setSettlements(settlementsRes.data);
      setEmployees(employeesRes.data.filter(e => e.employment_status !== 'Exit'));
      setStats(statsRes.data);
    } catch (error) {
      console.error(error);
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  // Check pending assets for employee
  const checkPendingAssets = async (employeeId) => {
    if (!employeeId) {
      setPendingAssets([]);
      setAssetsChecked(false);
      return;
    }
    
    setCheckingAssets(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/assets/pending-returns/${employeeId}`);
      if (response.ok) {
        const data = await response.json();
        setPendingAssets(data.assets || []);
        setAssetsChecked(true);
      }
    } catch (error) {
      console.error('Error checking pending assets:', error);
    } finally {
      setCheckingAssets(false);
    }
  };

  // Effect to check assets when employee changes
  useEffect(() => {
    if (formData.employee_id) {
      checkPendingAssets(formData.employee_id);
    }
  }, [formData.employee_id]);

  const handleCreateSettlement = async () => {
    if (!formData.employee_id || !formData.last_working_date) {
      toast.error('Please select employee and last working date');
      return;
    }
    
    // Block if pending assets exist
    if (pendingAssets.length > 0) {
      toast.error(`Cannot create FnF: ${pendingAssets.length} asset(s) pending return. Please ensure all assets are returned first.`);
      return;
    }
    
    try {
      await createFinalSettlement(formData);
      toast.success('Final settlement initiated successfully');
      setShowCreateModal(false);
      resetForm();
      setPendingAssets([]);
      setAssetsChecked(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create settlement');
    }
  };

  const handleApprove = async (id) => {
    try {
      await approveFinalSettlement(id);
      toast.success('Settlement approved successfully');
      fetchData();
      setShowDetailModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve');
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      toast.error('Please provide rejection reason');
      return;
    }
    try {
      await rejectFinalSettlement(selectedSettlement.id, rejectReason);
      toast.success('Settlement rejected');
      setShowRejectModal(false);
      setRejectReason('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject');
    }
  };

  const handleMarkProcessed = async (id) => {
    try {
      await markSettlementProcessed(id);
      toast.success('Settlement marked as processed');
      fetchData();
      setShowDetailModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process');
    }
  };

  const handleDownloadPDF = async (id) => {
    try {
      const response = await downloadFinalSettlementPDF(id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `final_settlement_${id}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  const resetForm = () => {
    setFormData({
      employee_id: '',
      separation_type: 'resignation',
      last_working_date: '',
      resignation_date: '',
      total_leave_balance: 0,
      leave_encashment_days: 0,
      notice_period_required_days: 60,
      notice_period_served_days: 0,
      notice_period_buyout: false,
      pending_reimbursements: 0,
      bonus_earned: 0,
      incentive_earned: 0,
      other_earnings: 0,
      other_earnings_remarks: '',
      loan_outstanding: 0,
      advance_outstanding: 0,
      notice_recovery: 0,
      other_deductions: 0,
      other_deductions_remarks: '',
      remarks: '',
      exit_id: ''
    });
  };

  const viewDetails = (settlement) => {
    setSelectedSettlement(settlement);
    setShowDetailModal(true);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const hasAccess = useSectionAccess('/final-settlement');
  if (hasAccess === false) {
    return (
      <Layout user={user} setUser={setUser}>
        <div className="flex flex-col items-center justify-center h-64">
          <AlertCircle className="h-16 w-16 text-red-500 mb-4" />
          <h2 className="text-xl font-semibold text-gray-900">Access Denied</h2>
          <p className="text-gray-500">You don't have permission to view this page.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} setUser={setUser}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <div className="flex items-center gap-3">
              {prefillData?.from_exit && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => navigate('/exit-management')}
                  className="gap-1"
                  data-testid="back-to-exit-btn"
                >
                  <ArrowLeft size={16} />
                  Exit Management
                </Button>
              )}
              <h1 className="text-2xl font-bold text-gray-900">Final Settlement</h1>
            </div>
            <p className="text-gray-500">Manage employee full & final settlements</p>
            {prefillData?.from_exit && prefillData?.employee_name && (
              <p className="text-sm text-indigo-600 mt-1 flex items-center gap-1">
                <ExternalLink size={14} />
                Linked from Exit: {prefillData.employee_name} ({prefillData.employee_code})
              </p>
            )}
          </div>
          <Button onClick={() => setShowCreateModal(true)} className="flex items-center gap-2" data-testid="create-settlement-btn">
            <Plus className="h-4 w-4" />
            Initiate Settlement
          </Button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
              <CardContent className="p-4 text-center">
                <Clock className="h-6 w-6 text-amber-600 mx-auto mb-2" />
                <p className="text-xs text-amber-600 font-medium">Pending</p>
                <p className="text-2xl font-bold text-amber-900">{stats.pending}</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
              <CardContent className="p-4 text-center">
                <CheckCircle className="h-6 w-6 text-blue-600 mx-auto mb-2" />
                <p className="text-xs text-blue-600 font-medium">Approved</p>
                <p className="text-2xl font-bold text-blue-900">{stats.approved}</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
              <CardContent className="p-4 text-center">
                <Banknote className="h-6 w-6 text-emerald-600 mx-auto mb-2" />
                <p className="text-xs text-emerald-600 font-medium">Processed</p>
                <p className="text-2xl font-bold text-emerald-900">{stats.processed}</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
              <CardContent className="p-4 text-center">
                <XCircle className="h-6 w-6 text-red-600 mx-auto mb-2" />
                <p className="text-xs text-red-600 font-medium">Rejected</p>
                <p className="text-2xl font-bold text-red-900">{stats.rejected}</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-indigo-50 to-indigo-100 border-indigo-200">
              <CardContent className="p-4 text-center">
                <CreditCard className="h-6 w-6 text-indigo-600 mx-auto mb-2" />
                <p className="text-xs text-indigo-600 font-medium">Total Paid</p>
                <p className="text-lg font-bold text-indigo-900">{formatCurrency(stats.total_processed_amount)}</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <Filter className="h-5 w-5 text-gray-400" />
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-48" data-testid="filter-status">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Settlements</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="processed">Processed</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Settlements List */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <UserMinus className="h-5 w-5" />
              Settlements ({settlements.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-32">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : settlements.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <UserMinus className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No settlements found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="text-left p-3 text-sm font-medium text-gray-600">Employee</th>
                      <th className="text-left p-3 text-sm font-medium text-gray-600">Department</th>
                      <th className="text-left p-3 text-sm font-medium text-gray-600">Separation</th>
                      <th className="text-left p-3 text-sm font-medium text-gray-600">Last Date</th>
                      <th className="text-right p-3 text-sm font-medium text-gray-600">Net Amount</th>
                      <th className="text-center p-3 text-sm font-medium text-gray-600">Status</th>
                      <th className="text-center p-3 text-sm font-medium text-gray-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {settlements.map(settlement => (
                      <tr key={settlement.id} className="hover:bg-gray-50" data-testid={`settlement-row-${settlement.id}`}>
                        <td className="p-3">
                          <div className="font-medium text-gray-900">{settlement.employee_name}</div>
                          <div className="text-xs text-gray-500">{settlement.employee_code}</div>
                        </td>
                        <td className="p-3 text-sm text-gray-600">{settlement.department}</td>
                        <td className="p-3 text-sm text-gray-600 capitalize">
                          {settlement.separation_type?.replace('_', ' ')}
                        </td>
                        <td className="p-3 text-sm text-gray-600">{settlement.last_working_date}</td>
                        <td className="p-3 text-right font-medium text-gray-900">
                          {formatCurrency(settlement.net_settlement_amount)}
                        </td>
                        <td className="p-3 text-center">
                          <Badge className={statusColors[settlement.status]}>
                            {settlement.status}
                          </Badge>
                        </td>
                        <td className="p-3 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <Button size="sm" variant="ghost" onClick={() => viewDetails(settlement)} data-testid={`view-btn-${settlement.id}`}>
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => handleDownloadPDF(settlement.id)} data-testid={`download-btn-${settlement.id}`}>
                              <Download className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create Settlement Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Initiate Final Settlement</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 py-4">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Employee *</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" role="combobox" className="w-full justify-between font-normal" data-testid="select-employee">
                      {formData.employee_id
                        ? (() => {
                            const emp = employees.find(e => e.id === formData.employee_id);
                            return emp ? `${emp.name || ''} (${emp.employee_id || ''})` : 'Select Employee';
                          })()
                        : 'Select Employee'}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[400px] p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Search employee name or code..." />
                      <CommandList>
                        <CommandEmpty>No employee found.</CommandEmpty>
                        <CommandGroup className="max-h-[250px] overflow-auto">
                          {employees.map(emp => (
                            <CommandItem
                              key={emp.id}
                              value={`${emp.name || ''} ${emp.employee_id || ''}`}
                              onSelect={() => setFormData({...formData, employee_id: emp.id})}
                            >
                              <Check className={`mr-2 h-4 w-4 ${formData.employee_id === emp.id ? 'opacity-100' : 'opacity-0'}`} />
                              <div>
                                <span className="font-medium">{emp.name || 'Unknown'}</span>
                                <span className="text-xs text-slate-400 ml-2">({emp.employee_id})</span>
                              </div>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Separation Type *</label>
                <Select value={formData.separation_type} onValueChange={(v) => setFormData({...formData, separation_type: v})}>
                  <SelectTrigger data-testid="select-separation-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {separationTypes.map(type => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Last Working Date *</label>
                <Input 
                  type="date" 
                  value={formData.last_working_date} 
                  onChange={(e) => setFormData({...formData, last_working_date: e.target.value})}
                  data-testid="input-lwd"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Resignation Date</label>
                <Input 
                  type="date" 
                  value={formData.resignation_date} 
                  onChange={(e) => setFormData({...formData, resignation_date: e.target.value})}
                />
              </div>
            </div>

            {/* Pending Assets Warning */}
            {formData.employee_id && assetsChecked && pendingAssets.length > 0 && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <AlertCircle className="text-red-600 mt-0.5" size={20} />
                  <div className="flex-1">
                    <h4 className="font-semibold text-red-800">Pending Asset Returns ({pendingAssets.length})</h4>
                    <p className="text-sm text-red-700 mt-1">
                      The following assets must be returned before FnF can be processed:
                    </p>
                    <ul className="mt-2 space-y-1">
                      {pendingAssets.map((asset, idx) => (
                        <li key={idx} className="text-sm text-red-700 flex items-center gap-2">
                          <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                          <span className="font-medium">{asset.asset_name}</span>
                          <span className="text-red-600 font-mono text-xs">({asset.serial_number})</span>
                          <span className="text-red-500">- {asset.asset_type}</span>
                        </li>
                      ))}
                    </ul>
                    <p className="text-xs text-red-600 mt-2">
                      Go to Asset Management → Assignments to process returns.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {formData.employee_id && assetsChecked && pendingAssets.length === 0 && (
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
                <CheckCircle className="text-green-600" size={18} />
                <span className="text-sm text-green-700">All company assets returned. Ready for FnF processing.</span>
              </div>
            )}

            {checkingAssets && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-2">
                <Clock className="text-blue-600 animate-spin" size={18} />
                <span className="text-sm text-blue-700">Checking pending assets...</span>
              </div>
            )}

            {/* Leave Details */}
            <div className="border-t pt-4">
              <h4 className="font-medium text-gray-900 mb-3">Leave Details</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Total Leave Balance</label>
                  <Input 
                    type="number" 
                    value={formData.total_leave_balance} 
                    onChange={(e) => setFormData({...formData, total_leave_balance: parseFloat(e.target.value) || 0})}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Leave Encashment Days</label>
                  <Input 
                    type="number" 
                    value={formData.leave_encashment_days} 
                    onChange={(e) => setFormData({...formData, leave_encashment_days: parseFloat(e.target.value) || 0})}
                    data-testid="input-leave-encashment"
                  />
                </div>
              </div>
            </div>

            {/* Notice Period */}
            <div className="border-t pt-4">
              <h4 className="font-medium text-gray-900 mb-3">Notice Period</h4>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Required Days</label>
                  <Input 
                    type="number" 
                    value={formData.notice_period_required_days} 
                    onChange={(e) => setFormData({...formData, notice_period_required_days: parseInt(e.target.value) || 0})}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Served Days</label>
                  <Input 
                    type="number" 
                    value={formData.notice_period_served_days} 
                    onChange={(e) => setFormData({...formData, notice_period_served_days: parseInt(e.target.value) || 0})}
                  />
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input 
                      type="checkbox" 
                      checked={formData.notice_period_buyout}
                      onChange={(e) => setFormData({...formData, notice_period_buyout: e.target.checked})}
                      className="rounded"
                    />
                    <span className="text-sm">Notice Buyout</span>
                  </label>
                </div>
              </div>
            </div>

            {/* Additional Earnings */}
            <div className="border-t pt-4">
              <h4 className="font-medium text-gray-900 mb-3">Additional Earnings</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Pending Reimbursements</label>
                  <Input 
                    type="number" 
                    value={formData.pending_reimbursements} 
                    onChange={(e) => setFormData({...formData, pending_reimbursements: parseFloat(e.target.value) || 0})}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Bonus Earned</label>
                  <Input 
                    type="number" 
                    value={formData.bonus_earned} 
                    onChange={(e) => setFormData({...formData, bonus_earned: parseFloat(e.target.value) || 0})}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Incentive Earned</label>
                  <Input 
                    type="number" 
                    value={formData.incentive_earned} 
                    onChange={(e) => setFormData({...formData, incentive_earned: parseFloat(e.target.value) || 0})}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Other Earnings</label>
                  <Input 
                    type="number" 
                    value={formData.other_earnings} 
                    onChange={(e) => setFormData({...formData, other_earnings: parseFloat(e.target.value) || 0})}
                  />
                </div>
              </div>
            </div>

            {/* Deductions */}
            <div className="border-t pt-4">
              <h4 className="font-medium text-gray-900 mb-3">Deductions</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Loan Outstanding</label>
                  <Input 
                    type="number" 
                    value={formData.loan_outstanding} 
                    onChange={(e) => setFormData({...formData, loan_outstanding: parseFloat(e.target.value) || 0})}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Advance Outstanding</label>
                  <Input 
                    type="number" 
                    value={formData.advance_outstanding} 
                    onChange={(e) => setFormData({...formData, advance_outstanding: parseFloat(e.target.value) || 0})}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Other Deductions</label>
                  <Input 
                    type="number" 
                    value={formData.other_deductions} 
                    onChange={(e) => setFormData({...formData, other_deductions: parseFloat(e.target.value) || 0})}
                  />
                </div>
              </div>
            </div>

            {/* Remarks */}
            <div className="border-t pt-4">
              <label className="text-sm font-medium text-gray-700 mb-1 block">Remarks</label>
              <Textarea 
                value={formData.remarks} 
                onChange={(e) => setFormData({...formData, remarks: e.target.value})}
                placeholder="Any additional notes..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>Cancel</Button>
            <Button onClick={handleCreateSettlement} data-testid="submit-settlement-btn">Create Settlement</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Settlement Details</DialogTitle>
          </DialogHeader>
          {selectedSettlement && (
            <div className="space-y-6 py-4">
              {/* Employee Info */}
              <div className="grid grid-cols-2 gap-4 bg-gray-50 p-4 rounded-lg">
                <div>
                  <p className="text-sm text-gray-500">Employee</p>
                  <p className="font-medium">{selectedSettlement.employee_name}</p>
                  <p className="text-xs text-gray-500">{selectedSettlement.employee_code}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Department / Designation</p>
                  <p className="font-medium">{selectedSettlement.department}</p>
                  <p className="text-xs text-gray-500">{selectedSettlement.designation}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Service Period</p>
                  <p className="font-medium">{selectedSettlement.date_of_joining} to {selectedSettlement.last_working_date}</p>
                  <p className="text-xs text-gray-500">{selectedSettlement.total_service_years} years</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Separation Type</p>
                  <p className="font-medium capitalize">{selectedSettlement.separation_type?.replace('_', ' ')}</p>
                </div>
              </div>

              {/* Salary Info */}
              <div className="grid grid-cols-4 gap-4">
                <Card>
                  <CardContent className="p-3 text-center">
                    <p className="text-xs text-gray-500">Monthly Gross</p>
                    <p className="font-bold text-lg">{formatCurrency(selectedSettlement.monthly_gross)}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-3 text-center">
                    <p className="text-xs text-gray-500">Monthly Basic</p>
                    <p className="font-bold text-lg">{formatCurrency(selectedSettlement.monthly_basic)}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-3 text-center">
                    <p className="text-xs text-gray-500">Per Day Salary</p>
                    <p className="font-bold text-lg">{formatCurrency(selectedSettlement.per_day_salary)}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-3 text-center">
                    <p className="text-xs text-gray-500">Gratuity Eligible</p>
                    <p className="font-bold text-lg">{selectedSettlement.gratuity_eligible ? 'Yes' : 'No'}</p>
                  </CardContent>
                </Card>
              </div>

              {/* Earnings & Deductions */}
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <h4 className="font-medium text-emerald-700 mb-3 flex items-center gap-2">
                    <TrendingUp className="h-4 w-4" /> Earnings
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between"><span>Pro-rata Salary</span><span>{formatCurrency(selectedSettlement.pro_rata_salary)}</span></div>
                    <div className="flex justify-between"><span>Leave Encashment ({selectedSettlement.leave_encashment_days} days)</span><span>{formatCurrency(selectedSettlement.leave_encashment_amount)}</span></div>
                    <div className="flex justify-between"><span>Gratuity</span><span>{formatCurrency(selectedSettlement.gratuity_amount)}</span></div>
                    <div className="flex justify-between"><span>Pending Reimbursements</span><span>{formatCurrency(selectedSettlement.pending_reimbursements)}</span></div>
                    <div className="flex justify-between"><span>Bonus</span><span>{formatCurrency(selectedSettlement.bonus_earned)}</span></div>
                    <div className="flex justify-between"><span>Incentive</span><span>{formatCurrency(selectedSettlement.incentive_earned)}</span></div>
                    <div className="flex justify-between"><span>Other Earnings</span><span>{formatCurrency(selectedSettlement.other_earnings)}</span></div>
                    <div className="flex justify-between font-bold border-t pt-2 text-emerald-700">
                      <span>Total Earnings</span><span>{formatCurrency(selectedSettlement.total_earnings)}</span>
                    </div>
                  </div>
                </div>
                <div>
                  <h4 className="font-medium text-red-700 mb-3 flex items-center gap-2">
                    <DollarSign className="h-4 w-4" /> Deductions
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between"><span>Notice Recovery ({selectedSettlement.notice_period_shortfall_days} days)</span><span>{formatCurrency(selectedSettlement.notice_recovery)}</span></div>
                    <div className="flex justify-between"><span>Loan Outstanding</span><span>{formatCurrency(selectedSettlement.loan_outstanding)}</span></div>
                    <div className="flex justify-between"><span>Advance Outstanding</span><span>{formatCurrency(selectedSettlement.advance_outstanding)}</span></div>
                    <div className="flex justify-between"><span>Other Deductions</span><span>{formatCurrency(selectedSettlement.other_deductions)}</span></div>
                    <div className="flex justify-between font-bold border-t pt-2 text-red-700">
                      <span>Total Deductions</span><span>{formatCurrency(selectedSettlement.total_deductions)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Net Settlement */}
              <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
                <CardContent className="p-4">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-medium text-blue-900">Net Settlement Amount</span>
                    <span className="text-2xl font-bold text-blue-900">{formatCurrency(selectedSettlement.net_settlement_amount)}</span>
                  </div>
                </CardContent>
              </Card>

              {/* Status & Actions */}
              <div className="flex items-center justify-between border-t pt-4">
                <div>
                  <Badge className={statusColors[selectedSettlement.status] + ' text-sm px-3 py-1'}>
                    {selectedSettlement.status?.toUpperCase()}
                  </Badge>
                  {selectedSettlement.approved_by_name && (
                    <p className="text-xs text-gray-500 mt-1">Approved by: {selectedSettlement.approved_by_name}</p>
                  )}
                  {selectedSettlement.rejection_reason && (
                    <p className="text-xs text-red-500 mt-1">Reason: {selectedSettlement.rejection_reason}</p>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => handleDownloadPDF(selectedSettlement.id)}>
                    <Download className="h-4 w-4 mr-2" /> Download PDF
                  </Button>
                  {user?.role === 'admin' && selectedSettlement.status === 'pending' && (
                    <>
                      <Button variant="destructive" onClick={() => { setShowRejectModal(true); setShowDetailModal(false); }} data-testid="reject-settlement-btn">
                        <XCircle className="h-4 w-4 mr-2" /> Reject
                      </Button>
                      <Button onClick={() => handleApprove(selectedSettlement.id)} data-testid="approve-settlement-btn">
                        <CheckCircle className="h-4 w-4 mr-2" /> Approve
                      </Button>
                    </>
                  )}
                  {user?.role === 'admin' && selectedSettlement.status === 'approved' && (
                    <Button onClick={() => handleMarkProcessed(selectedSettlement.id)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="process-settlement-btn">
                      <Banknote className="h-4 w-4 mr-2" /> Mark as Paid
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reject Modal */}
      <Dialog open={showRejectModal} onOpenChange={setShowRejectModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Settlement</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <label className="text-sm font-medium text-gray-700 mb-1 block">Rejection Reason *</label>
            <Textarea 
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Please provide a reason for rejection..."
              rows={4}
              data-testid="reject-reason-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRejectModal(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleReject} data-testid="confirm-reject-btn">Reject Settlement</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default FinalSettlement;
